import { getDeviceId, getSessionId } from '@amplitude/analytics-browser';
import { useUser } from '@auth0/nextjs-auth0/client';
import capitalize from 'lodash/capitalize';
import { useSearchParams } from 'next/navigation';
import qs from 'query-string';
import { useCallback, useRef } from 'react';

import type { SubscriptionsEligibilityValidationResponse } from '@calm-web/api-sdk/lib/atlas-subscriptions-api';
import type { GetV1UsersUserIdPrivacyFlagsResponseBody } from '@calm-web/api-sdk/lib/internal-sleep-api';

import { useAuthContext } from '@/context/auth/AuthContextProvider';
import { useAnalytics } from '@/hooks/analytics';
import { useApi } from '@/hooks/api';
import { useSetupUserAfterAuth } from '@/hooks/forms/useSetupUserAfterAuth';
import { useGuestPassRequestCode } from '@/hooks/guestPass/useGuestPassRequestCode';
import {
	useAuthFormModeState,
	useComplianceLevelState,
	usePartnerState,
	usePurchaseParamsState,
	useUserState,
} from '@/hooks/store';
import useIsCalmSleepApp from '@/hooks/useIsCalmSleepApp';
import { useAmplitudeExperiment } from '@/hooks/utils/experiments/useAmplitudeExperiment';
import { IS_GDPR_LIMITED, REQUIRE_GDPR_MODAL } from '@/store/complianceLevel/types';
import type { Subscription, User } from '@/store/user/types';
import { getCouponForLogging, getSlug } from '@/utils/analytics/setupEventProps';
import { getAuth0Endpoint } from '@/utils/auth0';
import { AmplitudeExperimentNames } from '@/utils/experiments/amplitudeExperiment';
import getUrl from '@/utils/getUrl';
import { CalmAuthMethods } from '@/utils/privacyCookies';

export enum BroadcastChannelName {
	TAB_COMMUNICATION = 'TAB_COMMUNICATION',
}

export enum BroadcastChannelEvent {
	LOGIN_COMPLETED = 'LOGIN_COMPLETED',
	SIGNUP_COMPLETED = 'SIGNUP_COMPLETED',
	WINDOW_CLOSED = 'WINDOW_CLOSED',
}

type SignupArgs = {
	authMethod: CalmAuthMethods;
	marketingOptIn: boolean;
	onAuthSuccess?: (user: User) => void;
	loginHint?: string;
};

interface UseAuth {
	authenticate: (args: SignupArgs) => void;
	isAuth0Enabled: boolean | undefined;
	isLoading: boolean;
	isAuth0User: boolean;
}

const forceAuth0Enabled = process.env.NEXT_PUBLIC_FORCE_AUTH0_ENABLED === 'true';

const hardcodedAuthEnabledPartners = ['test-calm-group-code', 'ews'];

export function useAuth(): UseAuth {
	const isCalmSleepApp = useIsCalmSleepApp();

	const searchParams = useSearchParams();
	const user = useUserState();

	const { checkSession } = useUser();

	const partnerSlug = searchParams?.get('partnerSlug');
	const pageShouldAlwaysShowAuth =
		typeof partnerSlug === 'string' ? hardcodedAuthEnabledPartners.includes(partnerSlug) : false;

	// the Sleep app should never use the old Auth0
	const shouldAlwaysShowAuth = forceAuth0Enabled || pageShouldAlwaysShowAuth || isCalmSleepApp;
	// don't need to call the experiment if it's always enabled
	const { isEnrolled, isLoading } = useAmplitudeExperiment(
		AmplitudeExperimentNames.AUTH0_LOGIN,
		shouldAlwaysShowAuth,
	);
	const apiRequest = useApi();

	const setupUserAfterAuth = useSetupUserAfterAuth();
	const { setIsAuthenticating } = useAuthContext();
	const authFormMode = useAuthFormModeState();
	const signupPopup = useRef<Window | null>(null);
	const { logEvent } = useAnalytics();
	const guestPassCode = useGuestPassRequestCode();
	const partner = usePartnerState();
	const purchaseParamsState = usePurchaseParamsState();

	const complianceLevel = useComplianceLevelState();

	const authenticateSleep = useCallback(
		({ authMethod, marketingOptIn, onAuthSuccess, loginHint }: SignupArgs) => {
			logEvent({
				eventName: `Login Form : ${capitalize(authMethod)} : Clicked`,
				eventProps: {
					mode: authFormMode,
				},
			});
			setIsAuthenticating(true);

			const channel = new BroadcastChannel(BroadcastChannelName.TAB_COMMUNICATION);
			channel.onmessage = async function (event) {
				const { type } = event.data;

				if (
					type === BroadcastChannelEvent.LOGIN_COMPLETED ||
					type === BroadcastChannelEvent.SIGNUP_COMPLETED
				) {
					// Close channel, so that we avoid catching any subsequent events (WINDOW_CLOSED in particular).
					channel.close();

					await checkSession();

					const { data: sessionUser } = await apiRequest<{
						'https://oneid.calm.com/calm_oneid'?: string;
						email?: string;
						given_name?: string;
						'https://oneid.calm.com/given_name'?: string;
					}>({
						service: 'calm-sleep-app-web-api',
						endpoint: 'auth/me',
						method: 'GET',
					});

					const sleepUser: User = {
						id: sessionUser['https://oneid.calm.com/calm_oneid'],
						email: sessionUser.email,
						name: sessionUser.given_name ?? sessionUser['https://oneid.calm.com/given_name'],
						current_auth_type: 'auth0',
					};

					try {
						const {
							data: { ldu_opt_in_at },
						} = await apiRequest<GetV1UsersUserIdPrivacyFlagsResponseBody>({
							service: 'calm-sleep-app-web-api',
							endpoint: 'sleep/ldu-opt-in-at',
							method: 'GET',
						});
						sleepUser.has_opted_in_limited_data_use = typeof ldu_opt_in_at === 'string';
					} catch (error) {
						// for local development, since we can't send requests to internal APIs
						if (process.env.NEXT_PUBLIC_CALM_ENV === 'local') {
							sleepUser.has_opted_in_limited_data_use = false;
						}
					}

					try {
						const sku = purchaseParamsState?.sku;
						if (!sku) {
							throw new Error();
						}
						const offerCode = purchaseParamsState?.offerCode;
						const partnershipId = purchaseParamsState?.partnershipId;
						const {
							data: { status },
						} = await apiRequest<SubscriptionsEligibilityValidationResponse>({
							service: 'calm-sleep-app-web-api',
							endpoint: 'sleep/eligibility-status',
							method: 'POST',
							body: {
								sku,
								offerCode,
								partnershipId,
							},
						});
						sleepUser.eligibility_validation_status = status;
					} catch (error) {
						sleepUser.eligibility_validation_status = undefined;
					}
					if (sleepUser.eligibility_validation_status === 'already-subscribed') {
						sleepUser.subscription = { valid: true } as Subscription;
					}

					try {
						await setupUserAfterAuth(sleepUser, authMethod);
						const authSuccess = onAuthSuccess?.(sleepUser);
						logEvent({
							eventName: `Login Form : ${capitalize(authMethod)} : Success`,
							eventProps: {
								mode: type === BroadcastChannelEvent.LOGIN_COMPLETED ? 'login' : 'signup',
							},
						});
						if (type === BroadcastChannelEvent.SIGNUP_COMPLETED) {
							logEvent({
								eventName: 'Account : User : Created',
								eventProps: {
									...(!!sleepUser?.id && { calm_user_id: sleepUser.id }),
								},
							});
						}
						return authSuccess;
					} catch (error) {
						logEvent({
							eventName: `Login Form : ${capitalize(authMethod)} : Error`,
							eventProps: {
								mode: type === BroadcastChannelEvent.LOGIN_COMPLETED ? 'login' : 'signup',
							},
						});
					} finally {
						setIsAuthenticating(false);
					}
				}

				if (type === BroadcastChannelEvent.WINDOW_CLOSED) {
					logEvent({
						eventName: `Login Form : ${capitalize(authMethod)} : Cancelled`,
						eventProps: {
							mode: authFormMode,
						},
					});
					channel.close();
					setIsAuthenticating(false);
				}
			};

			if (signupPopup.current) {
				signupPopup.current.focus();
			} else {
				const left = (window.innerWidth - 600) / 2 + window.screenX;
				const top = (window.innerHeight - 600) / 2 + window.screenY;
				signupPopup.current = window.open(
					`${getUrl('calm-sleep-app-web-api')}/auth/${getAuth0Endpoint(
						authMethod,
						authFormMode ?? 'signup',
					)}?${qs.stringify({
						marketing_opt_in: marketingOptIn,
						requires_gdpr_consent:
							complianceLevel === REQUIRE_GDPR_MODAL || complianceLevel === IS_GDPR_LIMITED,
						guest_pass_code: guestPassCode,
						partner_id: partner?.id,
						partner_type: [partner?.category?.toLowerCase() || null],
						slug: getSlug(),
						coupon: getCouponForLogging(purchaseParamsState?.coupon),
						login_hint: loginHint,
						// send the device id to universal login page to connect the device with the user
						['ext-amp_device_id']: getDeviceId(),
						['ext-amp_session_id']: getSessionId(),
					})}`,
					'_blank',
					`popup,width=600,height=600,top=${top},left=${left}`,
				);

				if (signupPopup.current) {
					const interval = setInterval(() => {
						if (signupPopup.current?.closed) {
							signupPopup.current = null;
							clearInterval(interval);
							const channel = new BroadcastChannel(BroadcastChannelName.TAB_COMMUNICATION);
							channel.postMessage({ type: BroadcastChannelEvent.WINDOW_CLOSED });
						}
					}, 500);
				}
			}
		},
		[
			apiRequest,
			authFormMode,
			guestPassCode,
			logEvent,
			partner?.category,
			partner?.id,
			purchaseParamsState?.coupon,
			purchaseParamsState?.sku,
			purchaseParamsState?.offerCode,
			purchaseParamsState?.partnershipId,
			setIsAuthenticating,
			setupUserAfterAuth,
			complianceLevel,
			checkSession,
		],
	);

	const authenticateClassic = useCallback(
		({ authMethod, marketingOptIn, onAuthSuccess, loginHint }: SignupArgs) => {
			logEvent({
				eventName: `Login Form : ${capitalize(authMethod)} : Clicked`,
				eventProps: {
					mode: authFormMode,
				},
			});
			setIsAuthenticating(true);

			const channel = new BroadcastChannel(BroadcastChannelName.TAB_COMMUNICATION);
			channel.onmessage = async function (event) {
				const { type } = event.data;

				if (
					type === BroadcastChannelEvent.LOGIN_COMPLETED ||
					type === BroadcastChannelEvent.SIGNUP_COMPLETED
				) {
					// Close channel, so that we avoid catching any subsequent events (WINDOW_CLOSED in particular).
					channel.close();

					await checkSession();

					try {
						const { data } = await apiRequest<User>({
							endpoint: 'v2/me',
							method: 'GET',
						});
						await setupUserAfterAuth(data, authMethod);
						const authSuccess = onAuthSuccess?.(data);
						logEvent({
							eventName: `Login Form : ${capitalize(authMethod)} : Success`,
							eventProps: {
								mode: type === BroadcastChannelEvent.LOGIN_COMPLETED ? 'login' : 'signup',
							},
						});
						if (type === BroadcastChannelEvent.SIGNUP_COMPLETED) {
							logEvent({
								eventName: 'Account : User : Created',
								eventProps: {
									...(!!data?.id && { calm_user_id: data.id }),
								},
							});
						}
						return authSuccess;
					} catch (error) {
						logEvent({
							eventName: `Login Form : ${capitalize(authMethod)} : Error`,
							eventProps: {
								mode: type === BroadcastChannelEvent.LOGIN_COMPLETED ? 'login' : 'signup',
							},
						});
					} finally {
						setIsAuthenticating(false);
					}
				}

				if (type === BroadcastChannelEvent.WINDOW_CLOSED) {
					logEvent({
						eventName: `Login Form : ${capitalize(authMethod)} : Cancelled`,
						eventProps: {
							mode: authFormMode,
						},
					});
					channel.close();
					setIsAuthenticating(false);
				}
			};

			if (signupPopup.current) {
				signupPopup.current.focus();
			} else {
				const left = (window.innerWidth - 600) / 2 + window.screenX;
				const top = (window.innerHeight - 600) / 2 + window.screenY;
				signupPopup.current = window.open(
					`${getUrl('web-api')}/auth/${getAuth0Endpoint(authMethod, authFormMode ?? 'signup')}?${qs.stringify(
						{
							marketing_opt_in: marketingOptIn,
							guest_pass_code: guestPassCode,
							partner_id: partner?.id,
							partner_type: [partner?.category?.toLowerCase() || null],
							slug: getSlug(),
							coupon: getCouponForLogging(purchaseParamsState?.coupon),
							login_hint: loginHint,
							// send the device id to universal login page to connect the device with the user
							['ext-amp_device_id']: getDeviceId(),
							['ext-amp_session_id']: getSessionId(),
						},
					)}`,
					'_blank',
					`popup,width=600,height=600,top=${top},left=${left}`,
				);

				if (signupPopup.current) {
					const interval = setInterval(() => {
						if (signupPopup.current?.closed) {
							signupPopup.current = null;
							clearInterval(interval);
							const channel = new BroadcastChannel(BroadcastChannelName.TAB_COMMUNICATION);
							channel.postMessage({ type: BroadcastChannelEvent.WINDOW_CLOSED });
						}
					}, 500);
				}
			}
		},
		[
			apiRequest,
			authFormMode,
			guestPassCode,
			logEvent,
			partner?.category,
			partner?.id,
			purchaseParamsState?.coupon,
			setIsAuthenticating,
			setupUserAfterAuth,
			checkSession,
		],
	);

	const authenticate = isCalmSleepApp ? authenticateSleep : authenticateClassic;

	// should fallback to isEnrolled if shouldAlwaysShowAuth is false
	const isAuth0Enabled = shouldAlwaysShowAuth || isEnrolled;

	return { isAuth0Enabled, authenticate, isLoading, isAuth0User: user?.current_auth_type === 'auth0' };
}
